import { useLingui } from '@lingui/react/macro';
import { ArrowRight } from 'iconsax-reactjs';
import type { FC } from 'react';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import { DisplayQuoteToggleGroup } from '@/settings/DisplayQuoteToggleGroup';
import { cn } from '@/shared/react';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { CryptocurrencyCard } from './fields/CryptocurrencyCard';
import { CryptocurrencyCardLoading } from './fields/CryptocurrencyCardLoading';
import { useTradeTopCryptocurrencies } from './fields/useTradeTopCryptocurrencies';

export const TradeTopCryptocurrencies: FC<{
  className?: string;
  hideViewAll?: boolean;
}> = ({ className, hideViewAll = false }) => {
  const { t } = useLingui();
  const [settings] = useSettings();
  const { data } = useTradeTopCryptocurrencies(settings.displayQuote);
  usePupeteerSignal(!!data);

  return (
    <section
      id="trade-top-cryptocurrencies"
      className={cn(
        'relative bg-background-disabled/40 border border-border-disabled',
        className,
      )}
    >
      <div className="px-3 py-11 mx-auto max-w-desktop">
        <div className="flex desktop:flex-row flex-col desktop:items-end items-center desktop:justify-between justify-start pb-4 gap-4 desktop:gap-6">
          <div className="text-start mb-widget w-full">
            <h2 className="text-2xl font-medium mb-2">
              {t`Trade top cryptocurrencies`}
            </h2>
            <p className="text-sm text-content-secondary">
              {t`Buy & sell the most popular cryptocurrencies.`}
            </p>
          </div>

          <div className="desktop:mb-6 desktop:w-max w-full flex flex-col items-stretch desktop:items-end gap-3">
            {!hideViewAll && (
              <div>
                <Link
                  to={`/${settings.language}/buy-and-sell`}
                  className="flex items-center gap-2 whitespace-nowrap text-sm text-content-link"
                >
                  <span>{t`View All Coins`}</span>
                  <ArrowRight className="size-6 auto-rtl" />
                </Link>
              </div>
            )}
            <DisplayQuoteToggleGroup
              className="hidden desktop:flex w-full desktop:w-max"
              size={6}
              variant="button"
            />
            <DisplayQuoteToggleGroup
              className="flex desktop:hidden w-full desktop:w-max"
              size={8}
              variant="button"
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 desktop:grid desktop:grid-cols-3 desktop:gap-6">
          {!data ? (
            <>
              <CryptocurrencyCardLoading />
              <CryptocurrencyCardLoading />
              <CryptocurrencyCardLoading />
            </>
          ) : (
            data.map((list, index) => (
              <div key={list.title}>
                <CryptocurrencyCard
                  list={list}
                  quote={settings.displayQuote}
                  isFirstCard={index === 0}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
